import { routesNotOn } from "@/games/domains";
export interface DomainConfig {
  // Domain-specific restrictions
  gumgum: {
    disabledRoutes: string[];
    redirectTo: string;
  };
  tcgfan: {
    disabledRoutes: string[];
    redirectTo: string;
  };
  // Default behavior for localhost and other domains
  default: {
    disabledRoutes: string[];
    redirectTo: string | null;
  };
}

const restrictedConfig: DomainConfig = {
  gumgum: {
    disabledRoutes: routesNotOn("gumgum"),
    redirectTo: "https://tcgfan.com",
  },
  tcgfan: {
    disabledRoutes: routesNotOn("tcgfan"),
    redirectTo: "https://gumgum.gg",
  },
  default: {
    disabledRoutes: [],
    redirectTo: null,
  },
};

/** Nothing restricted - used when ENABLE_DOMAIN_RESTRICTIONS is off. */
const unrestrictedConfig: DomainConfig = {
  gumgum: { disabledRoutes: [], redirectTo: "https://tcgfan.com" },
  tcgfan: { disabledRoutes: [], redirectTo: "https://gumgum.gg" },
  default: { disabledRoutes: [], redirectTo: null },
};

export function getDomainConfig(): DomainConfig {
  // Check if domain restrictions are enabled via environment variable
  const enableDomainRestrictions =
    process.env.ENABLE_DOMAIN_RESTRICTIONS === "true";

  // If NEXT_PUBLIC_WEBSITE_CONFIG is set, always enable restrictions for testing
  const websiteConfig = process.env.NEXT_PUBLIC_WEBSITE_CONFIG;
  const shouldEnableRestrictions = enableDomainRestrictions || websiteConfig;

  // This previously returned the same config on both branches, so
  // ENABLE_DOMAIN_RESTRICTIONS had no effect at all.
  return shouldEnableRestrictions ? restrictedConfig : unrestrictedConfig;
}

export function getCurrentDomain(
  host: string,
): "gumgum" | "tcgfan" | "default" {
  // Check for NEXT_PUBLIC_WEBSITE_CONFIG first (for testing)
  const websiteConfig = process.env.NEXT_PUBLIC_WEBSITE_CONFIG;
  if (websiteConfig === "tcgfan") {
    return "tcgfan";
  } else if (websiteConfig === "gumgum") {
    return "gumgum";
  } else if (websiteConfig === "local") {
    return "default";
  }

  // Fall back to host-based detection for production
  if (
    host.includes("gumgum.gg") ||
    host.includes("gumgum.local") ||
    host.includes("gumgum-test")
  ) {
    return "gumgum";
  } else if (
    host.includes("tcgfan.com") ||
    host.includes("tcgfan.local") ||
    host.includes("tcgfan-test")
  ) {
    return "tcgfan";
  }
  return "default";
}

export function isRouteDisabled(
  pathname: string,
  domain: "gumgum" | "tcgfan" | "default",
): boolean {
  const config = getDomainConfig();
  const domainConfig = config[domain];

  // Add debugging for route checking
  if (process.env.NODE_ENV === "development") {
    console.log("Route disabled check:", {
      pathname,
      domain,
      disabledRoutes: domainConfig.disabledRoutes,
      matches: domainConfig.disabledRoutes.filter((route) =>
        pathname.startsWith(route),
      ),
    });
  }

  return domainConfig.disabledRoutes.some((route) =>
    pathname.startsWith(route),
  );
}

export function getRedirectUrl(pathname: string, host: string): string | null {
  const domain = getCurrentDomain(host);
  const config = getDomainConfig();
  const domainConfig = config[domain];

  // Add debugging when in development
  if (process.env.NODE_ENV === "development") {
    console.log("Domain redirect check:", {
      host,
      domain,
      pathname,
      isDisabled: isRouteDisabled(pathname, domain),
      redirectTo: domainConfig.redirectTo,
      websiteConfig: process.env.NEXT_PUBLIC_WEBSITE_CONFIG,
      enableDomainRestrictions: process.env.ENABLE_DOMAIN_RESTRICTIONS,
    });
  }

  // Never redirect sitemap.xml or ads.txt - these must be accessible on their respective domains
  if (pathname === "/sitemap.xml" || pathname === "/ads.txt") {
    return null;
  }

  // Special handling for root path on tcgfan.com - let client-side handle the redirect
  // Client-side localStorage-based redirect handled by GameRedirect component
  if (domain === "tcgfan" && pathname === "/") {
    // Don't redirect at middleware level - let client decide based on localStorage
    return null;
  }

  // Check if route is disabled and should redirect
  if (!isRouteDisabled(pathname, domain) || !domainConfig.redirectTo) {
    return null;
  }

  // For all other disabled routes, use the configured redirect
  return `${domainConfig.redirectTo}${pathname}`;
}
